/**
 * Helpers shared across the Outlook-style /email-viewer components.
 *
 * Kept dependency-free so they're easy to unit-test later; everything in
 * here is pure (no React, no fetch, no DOM access).
 */

/**
 * Pull a display name out of an RFC 5322-style ``From`` value.
 *
 * - ``"Alice Liu <alice@x.com>"`` -> ``"Alice Liu"``
 * - ``"alice@x.com"``             -> ``"alice@x.com"``
 * - ``"<alice@x.com>"``           -> ``"alice@x.com"``
 * - ``null`` / ``""``             -> ``"(no sender)"``
 *
 * Strips surrounding quotes from the name when present (Outlook quotes
 * names containing punctuation, e.g. ``"Liu, Alice" <alice@x.com>``).
 */
export function parseDisplayName(rawAddress: string | null | undefined): string {
  if (!rawAddress) return '(no sender)';
  const trimmed = rawAddress.trim();
  if (trimmed === '') return '(no sender)';

  const angleStart = trimmed.indexOf('<');
  if (angleStart < 0) return trimmed;

  if (angleStart === 0) {
    // "<alice@x.com>" -> "alice@x.com"
    const end = trimmed.indexOf('>');
    if (end > 0) return trimmed.slice(1, end);
    return trimmed;
  }

  const name = trimmed.slice(0, angleStart).trim();
  // Drop matching surrounding quotes.
  const dequoted =
    (name.startsWith('"') && name.endsWith('"')) ||
    (name.startsWith("'") && name.endsWith("'"))
      ? name.slice(1, -1).trim()
      : name;
  return dequoted || trimmed.slice(angleStart);
}

/** Extract the email address (``alice@x.com``) from an RFC 5322 value. */
export function parseEmailAddress(rawAddress: string | null | undefined): string | null {
  if (!rawAddress) return null;
  const trimmed = rawAddress.trim();
  if (trimmed === '') return null;

  const match = trimmed.match(/<([^>]+)>/);
  if (match) return match[1].trim();
  // No angle brackets — assume the whole string is the address.
  return trimmed;
}

/**
 * Two-letter initials for the avatar chip. Falls back to the first two
 * characters of the address local-part when there's no display name.
 */
export function initialsFor(rawAddress: string | null | undefined): string {
  const display = parseDisplayName(rawAddress);
  if (display && display !== '(no sender)' && !display.includes('@')) {
    const parts = display.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
  }
  const addr = parseEmailAddress(rawAddress) ?? display;
  const local = addr.split('@')[0] ?? '';
  return local.slice(0, 2).toUpperCase() || '?';
}

/**
 * Deterministic Tailwind background-colour class for the avatar chip.
 *
 * Same sender -> same colour across renders. Uses a small palette tuned
 * for white text legibility.
 */
const AVATAR_PALETTE = [
  'bg-rose-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-lime-600',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-cyan-600',
  'bg-sky-600',
  'bg-blue-600',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-fuchsia-500',
  'bg-pink-500',
];

function hashString(input: string): number {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) & 0xffffffff;
  }
  return Math.abs(h);
}

export function avatarColorClass(rawAddress: string | null | undefined): string {
  const key = parseEmailAddress(rawAddress) ?? parseDisplayName(rawAddress) ?? '';
  return AVATAR_PALETTE[hashString(key.toLowerCase()) % AVATAR_PALETTE.length];
}

/**
 * Deterministic ``mm/dd/yyyy`` label for the message-list row.
 *
 * Locale-independent and zero-padded so every label is exactly 10 chars —
 * keeps row widths stable and avoids the per-locale ordering ambiguity that
 * ``toLocaleDateString`` would otherwise introduce. Falls back to the empty
 * string when ``isoDate`` is null or unparseable.
 */
export function formatRowDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
}

/** Long-form date label for the reading-pane header (e.g. ``Mon, Jan 15, 2024, 2:34 PM``). */
export function formatReaderDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Human-readable byte count: ``1.2 MB``, ``843 KB``, ``512 B``. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx++;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIdx]}`;
}

/** Trailing path segment of an S3 key, for the breadcrumb. */
export function s3KeyBasename(s3Key: string): string {
  if (!s3Key) return '';
  const trimmed = s3Key.replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}
